import { useTranslation } from 'react-i18next';
import { Button, cn, SearchInput } from '@c2fo/liquidity';
import usePlayableInvoiceStats from '@/enterprise/data/usePlayableInvoiceStats';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useLocaleFormat from '@/utils/useLocaleFormat';
import Skeleton from '../../Skeleton';

const AmountTabsLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="flex w-full flex-wrap-reverse justify-between gap-x-10 gap-y-4 border-b border-stroke">
    {children}
  </div>
);

export interface AmountTabProps {
  amount: number;
  count: number;
  currency: string;
  activeTab: 'included' | 'excluded';
  label: string;
  setActiveTab: (tab: 'included' | 'excluded') => void;
  value: 'included' | 'excluded';
  makerOrgUuid: string;
  marketUuid: string;
  takerUuid: string;
}

const AmountTab = ({
  amount,
  count,
  currency,
  label,
  setActiveTab,
  value,
  makerOrgUuid,
  marketUuid,
  takerUuid,
  activeTab,
}: AmountTabProps) => {
  const { t } = useTranslation();
  const { asCurrency, asNumber } = useLocaleFormat();
  const { trackEnterpriseEvent } = useReporting();

  const handleTabClick = () => {
    trackEnterpriseEvent('invoices::tab-changed', {
      makerOrgUuid,
      marketUuid,
      takerUuid,
      fromTab: activeTab,
      toTab: value,
    });

    setActiveTab?.(value);
  };

  return (
    <div
      className={cn(
        'group flex shrink-0 cursor-pointer justify-between gap-10 rounded-t-xl border-l border-r border-t border-gray-200 bg-canvas p-4 transition-colors',
        {
          '-mb-px border-stroke bg-white': activeTab === value,
        }
      )}
      onClick={handleTabClick}
      onKeyDown={(e) => e.key === 'Enter' && handleTabClick()}
      role="button"
      tabIndex={0}
    >
      <div
        className={cn('text-sm font-medium transition-colors group-hover:text-text-primary', {
          'text-text-secondary': activeTab !== value,
        })}
      >
        {label}
      </div>
      <div className="text-right text-sm">
        <div className="text-text-secondary">
          {t('core.invoiceWithCount', {
            count,
            formattedCount: asNumber({ value: count }),
          })}
        </div>
        <div
          className={cn('text-xl font-medium transition-colors group-hover:text-text-primary', {
            'text-text-secondary': activeTab !== value,
          })}
        >
          {asCurrency(amount, currency)}
        </div>
      </div>
    </div>
  );
};

export interface AmountTabsProps {
  activeTab: 'included' | 'excluded';
  handleSearch: (searchInput: string) => void;
  takerMarket: TakerMarket;
  searchInput: string;
  searchValue: string;
  setActiveTab: (tab: 'included' | 'excluded') => void;
  setSearchInput: (searchInput: string) => void;
}

const AmountTabsComponent = ({
  activeTab,
  handleSearch,
  searchInput,
  searchValue,
  setActiveTab,
  setSearchInput,
  takerMarket,
}: AmountTabsProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { data: playableInvoiceStats } = usePlayableInvoiceStats({
    makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    takerMarkets: [takerMarket],
    ...(searchValue && { search: { voucherPatterns: [searchValue] } }),
  });
  const includedAmount = playableInvoiceStats.includedAmount;
  const includedCount = playableInvoiceStats.includedCount;
  const excludedAmount = playableInvoiceStats.excludedAmount;
  const excludedCount = playableInvoiceStats.excludedCount;

  const trackSearchEvent = () => {
    trackEnterpriseEvent('invoices::searched', {
      makerOrgUuid: takerMarket.makerOrganizationUuid,
      marketUuid: takerMarket.marketUuid,
      takerUuid: takerMarket.takerDivisionUuid,
      currentTab: activeTab,
    });
  };

  const handleSearchClick = () => {
    trackSearchEvent();
    handleSearch(searchInput);
  };

  const handleSearchKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      trackSearchEvent();
      handleSearch(searchInput);
    }
  };

  const tabs = [
    {
      amount: includedAmount,
      count: includedCount,
      label: t('core.included'),
      value: 'included',
    },
    {
      amount: excludedAmount,
      count: excludedCount,
      label: t('core.excluded'),
      value: 'excluded',
    },
  ] as const;

  return (
    <AmountTabsLayout>
      <IntercomDataTarget className="-mb-px overflow-x-auto pb-px" target="invoiceEditTabs">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <AmountTab
              key={tab.value}
              amount={tab.amount}
              count={tab.count}
              currency={takerMarket.currency}
              label={tab.label}
              setActiveTab={setActiveTab}
              value={tab.value}
              activeTab={activeTab}
              makerOrgUuid={takerMarket.makerOrganizationUuid}
              marketUuid={takerMarket.marketUuid}
              takerUuid={takerMarket.takerDivisionUuid}
            />
          ))}
        </div>
      </IntercomDataTarget>
      <div className="flex w-full flex-col items-center gap-3 sm:w-fit sm:flex-row">
        <SearchInput
          className="w-full sm:w-60"
          onChange={(e) => setSearchInput(e.target.value)}
          onClear={() => {
            setSearchInput('');
            handleSearch('');
          }}
          onKeyUp={handleSearchKeyUp}
          placeholder={t('invoices.searchInputPlaceholder')}
          value={searchInput}
          withSearchIcon
        />
        <Button className="w-full sm:w-fit" onClick={handleSearchClick} variant="secondary">
          {t('core.search')}
        </Button>
      </div>
    </AmountTabsLayout>
  );
};

const AmountTabs = (props: AmountTabsProps) => (
  <QueryBoundaries
    LoadingComponent={() => (
      <AmountTabsLayout>
        <div className="flex gap-1">
          {Array.from({ length: 2 }).map((_, index) => (
            <div
              key={index}
              className={cn(
                'group flex w-72 justify-between gap-10 rounded-t-lg border-x border-t border-gray-200 bg-canvas p-4',
                {
                  '-mb-px border-stroke bg-white': index === 0,
                }
              )}
            >
              <div>
                <Skeleton className="h-5 w-20" />
              </div>
              <div className="flex flex-col items-end">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="mt-1 h-6 w-32" />
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center">
          <Skeleton className="h-12 w-72" />
        </div>
      </AmountTabsLayout>
    )}
  >
    <AmountTabsComponent {...props} />
  </QueryBoundaries>
);

export default AmountTabs;
